// Implements: F-IDN-027, F-UIX-018, F-UIX-010
/**
 * Personas (mockup v3 P3, `vPeople`): who is in one organization, with which role and in which
 * teams. Mount it keyed by the organization's id (checklist 14): every read is keyed by it too, so
 * an answer of another organization never reaches this screen.
 *
 * A role change asks first, then sends one `PATCH`; the select always shows the server's value, so
 * a refusal (a `409`, a `403`) leaves it where it was and says why. "Quitar de la organización"
 * moves the person's mockups to whom the admin picks and only then removes them; a refused move
 * keeps the person and the dialog open with the server's sentence. Writes are never retried.
 * Owner 2026-10-19: the recipients are only the people who can receive every one of those mockups
 * (read from the server for the person being removed); with nothing to move, nothing is moved.
 * If mockups arrived meanwhile the server refuses the removal (`still_owns_mockups`): the answer
 * is read again and the dialog asks for a recipient.
 */

import { useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";

import { asApiError } from "../../../shared/api/asApiError";
import type { ApiError } from "../../../shared/api/ApiError";
import { useTranslation } from "../../../shared/i18n";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { SearchField } from "../../../shared/ui/molecules/SearchField";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import type { OrgRole, PersonView } from "../../../types/organization";
import styles from "../components/Organization.module.css";
import { PeopleTable } from "../components/PeopleTable";
import { RemovePersonDialog } from "../components/RemovePersonDialog";
import {
  usePeopleList,
  useReassignCandidates,
  useRemovePerson,
  useSetPersonRole,
  useTeamList,
} from "../hooks/usePeople";
import { matchesPerson, teamsOfPerson } from "../model/people";
import { isOrgAdmin, roleLabelKey } from "../model/roles";

export const TEAMS_PATH = "/backoffice/teams";

export interface PeopleContainerProps {
  readonly organizationId: string;
  readonly currentUserId: string;
}

interface PendingRole {
  readonly person: PersonView;
  readonly role: OrgRole;
}

/** The server's refusal of a removal whose person still owns mockups here (verifier round 1). */
function stillOwnsMockups(error: ApiError): boolean {
  return error.details?.field === "user_id" && error.details.reason === "still_owns_mockups";
}

/** The server's sentence for a refused write; a conflict says why in Spanish too. */
export function writeProblem(error: ApiError, fallback: string): string {
  return userMessage(error, fallback, ["CONFLICT"]);
}

export function PeopleContainer({ organizationId, currentUserId }: PeopleContainerProps): ReactElement {
  const { t } = useTranslation();
  const people = usePeopleList(organizationId);
  const teams = useTeamList(organizationId);
  const setRole = useSetPersonRole(organizationId);
  const remove = useRemovePerson(organizationId);
  const [query, setQuery] = useState("");
  const [pendingRole, setPendingRole] = useState<PendingRole | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [removing, setRemoving] = useState<PersonView | null>(null);
  const [removeProblem, setRemoveProblem] = useState<string | null>(null);
  const candidates = useReassignCandidates(organizationId, removing?.user.id ?? null);
  // A press that lands before the busy state renders must not send the removal twice.
  const removeInFlight = useRef(false);

  const all = useMemo(() => people.data?.people ?? [], [people.data]);
  // The organization's only admin cannot be removed (Default P3-4: the server refuses it with 409).
  const admins = all.filter((person) => isOrgAdmin(person.role));
  const soleAdminId = admins.length === 1 ? (admins[0]?.user.id ?? null) : null;
  const rows = useMemo(
    () =>
      all
        .filter((person) => matchesPerson(person, query))
        .map((person) => ({ person, teams: teamsOfPerson(teams.data?.teams ?? [], person.user.id) })),
    [all, query, teams.data],
  );

  function confirmRole(): void {
    if (pendingRole === null) return;
    const { person, role } = pendingRole;
    setPendingRole(null);
    setProblem(null);
    setRole.mutate(
      { userId: person.user.id, role },
      {
        onError: (error) => {
          setProblem(writeProblem(asApiError(error), t("common.state.error")));
        },
      },
    );
  }

  function confirmRemove(recipientId: string | null): void {
    if (removing === null || removeInFlight.current) return;
    removeInFlight.current = true;
    setRemoveProblem(null);
    remove.mutate(
      { userId: removing.user.id, recipientId },
      {
        onSuccess: () => {
          setRemoving(null);
        },
        onError: (error) => {
          const refused = asApiError(error);
          if (stillOwnsMockups(refused)) {
            // Verifier round 1: mockups arrived after the dialog read "none"; read again and ask.
            setRemoveProblem(t("organization.remove.stillOwns"));
            void candidates.refetch();
            return;
          }
          setRemoveProblem(writeProblem(refused, t("common.state.error")));
        },
        onSettled: () => {
          removeInFlight.current = false;
        },
      },
    );
  }

  function body(): ReactElement {
    if (people.isError) {
      return (
        <ErrorState
          headingLevel={3}
          error={asApiError(people.error)}
          onRetry={() => {
            void people.refetch();
          }}
        />
      );
    }
    if (people.data === undefined) return <LoadingState label={t("organization.people.loading")} />;
    if (all.length <= 1) {
      return (
        <EmptyState
          headingLevel={3}
          icon="users"
          title={t("organization.people.empty.title")}
          body={t("organization.people.empty.body")}
        />
      );
    }
    if (rows.length === 0) {
      return (
        <EmptyState
          headingLevel={3}
          icon="search"
          title={t("organization.people.noMatch.title")}
          body={t("organization.people.noMatch.body")}
        />
      );
    }
    return (
      <PeopleTable
        rows={rows}
        currentUserId={currentUserId}
        soleAdminId={soleAdminId}
        teamHref={(teamId) => `${TEAMS_PATH}/${encodeURIComponent(teamId)}`}
        onRoleChange={(person, role) => {
          setPendingRole({ person, role });
        }}
        onRemove={(person) => {
          setRemoveProblem(null);
          setRemoving(person);
        }}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <SearchField
          label={t("organization.people.search")}
          placeholder={t("organization.people.searchPlaceholder")}
          value={query}
          onChange={setQuery}
        />
        <span className={styles.spacer} />
        {people.data === undefined ? null : (
          <span className={styles.meta}>
            {t("organization.people.count", {
              people: all.length,
              admins: admins.length,
            })}
          </span>
        )}
      </div>
      {problem === null ? null : (
        <p className={styles.problem} role="alert">
          {problem}
        </p>
      )}
      {body()}
      <ConfirmDialog
        open={pendingRole !== null}
        title={t("organization.people.confirmRole.title")}
        body={
          pendingRole === null
            ? ""
            : t("organization.people.confirmRole.body", {
                name: pendingRole.person.user.full_name,
                role: t(roleLabelKey(pendingRole.role)),
              })
        }
        confirmLabel={t("organization.people.confirmRole.confirm")}
        cancelLabel={t("common.action.cancel")}
        onConfirm={confirmRole}
        onCancel={() => {
          setPendingRole(null);
        }}
      />
      {removing === null ? null : (
        <RemovePersonDialog
          key={removing.user.id}
          person={removing.user}
          // Only people who can receive every mockup are offered; the dialog preselects the first.
          candidates={candidates.data}
          candidatesProblem={candidates.isError ? writeProblem(asApiError(candidates.error), t("common.state.error")) : null}
          currentUserId={currentUserId}
          pending={remove.isPending}
          problem={removeProblem}
          onConfirm={confirmRemove}
          onCancel={() => {
            setRemoving(null);
          }}
        />
      )}
    </div>
  );
}
